import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AiContractImportResponse,
  BaseApiResponse,
  CdContractStatus,
  ContractTemplateDetailResponse,
  ContractTemplateListItem,
  CreateContractTemplateRequest,
  ImportSourceResponse,
  UpdateContractTemplateRequest,
} from "~/types/common";

const LIST_KEY = ["business-contract-template-list"] as const;
const detailKey = (seq: number) => ["business-contract-template", seq] as const;

const unwrapSuccess = <T,>(response: BaseApiResponse<T>, action: string): T => {
  if (!response || response.statusCode !== 200) {
    throw new Error(response?.message || `${action} 실패 (statusCode=${response?.statusCode ?? "unknown"})`);
  }
  return response.data as T;
};

/* ─── 목록 ─── */
const fetchTemplateList = async (): Promise<ContractTemplateListItem[]> => {
  const { data } = await privateHttp.get<BaseApiResponse<ContractTemplateListItem[]>>(
    API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.LIST
  );
  return unwrapSuccess(data, "계약서 양식 목록 조회") ?? [];
};

export const useContractTemplateList = () =>
  useQuery({ queryKey: LIST_KEY, queryFn: fetchTemplateList });

/* ─── 상세 ─── */
const fetchTemplateDetail = async (seq: number): Promise<ContractTemplateDetailResponse> => {
  const { data } = await privateHttp.get<BaseApiResponse<ContractTemplateDetailResponse>>(
    API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.DETAIL(seq)
  );
  return unwrapSuccess(data, "계약서 양식 상세 조회");
};

export const useContractTemplateDetail = (seq?: number) =>
  useQuery({
    queryKey: detailKey(seq ?? 0),
    queryFn: () => fetchTemplateDetail(seq!),
    enabled: !!seq,
  });

/* ─── 생성 ─── */
export const useCreateContractTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreateContractTemplateRequest): Promise<number> => {
      const { data } = await privateHttp.post<BaseApiResponse<number>>(
        API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.CREATE,
        req
      );
      return unwrapSuccess(data, "계약서 양식 생성");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
};

/* ─── 수정 ─── */
export const useUpdateContractTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { contractSeq: number; req: UpdateContractTemplateRequest }) => {
      const { data } = await privateHttp.put<BaseApiResponse<void>>(
        API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.UPDATE(params.contractSeq),
        params.req
      );
      unwrapSuccess(data, "계약서 양식 수정");
    },
    onSuccess: (_, params) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: detailKey(params.contractSeq) });
    },
  });
};

/* ─── 삭제 ─── */
export const useDeleteContractTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contractSeq: number) => {
      const { data } = await privateHttp.delete<BaseApiResponse<void>>(
        API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.DELETE(contractSeq)
      );
      unwrapSuccess(data, "계약서 양식 삭제");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
};

/* ─── 상태 단축 변경 (활성/비활성) ─── */
export const useChangeContractTemplateStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      contractSeq: number;
      status: CdContractStatus;
      /** 도장 필드가 있는 계약서 활성화 시 필요한 도장 PIN. */
      pin?: string;
    }) => {
      const { data } = await privateHttp.patch<BaseApiResponse<void>>(
        API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.STATUS(params.contractSeq),
        null,
        {
          params: {
            cdContractStatus: params.status,
            ...(params.pin ? { pin: params.pin } : {}),
          },
        }
      );
      unwrapSuccess(data, "계약서 양식 상태 변경");
    },
    onSuccess: (_, params) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: detailKey(params.contractSeq) });
    },
  });
};

/* ─── 임포트 원본 업로드 ─── */
export const useUploadImportSource = () =>
  useMutation({
    mutationFn: async (file: File): Promise<ImportSourceResponse> => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await privateHttp.post<BaseApiResponse<ImportSourceResponse>>(
        API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.IMPORT_SOURCE,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return unwrapSuccess(data, "계약서 원본 업로드");
    },
  });

/**
 * AI 자동 변환 — PDF/DOCX 를 Gemini 로 분석해 docJson + 필드 배치 초안 생성.
 * 성공 시 편집기에서 사용자 확인 후 create-template 로 최종 저장.
 * 크레딧 차감은 BE 에서 자동 처리 (사전 차감, 실패 시 환원).
 */
export const useAiImportContract = () =>
  useMutation({
    mutationFn: async (file: File): Promise<AiContractImportResponse> => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await privateHttp.post<BaseApiResponse<AiContractImportResponse>>(
        API_ENDPOINTS.BUSINESS_CONTRACT_TEMPLATE.IMPORT_AI,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120_000, // Gemini 응답까지 최장 90초 + 여유
        }
      );
      return unwrapSuccess(data, "AI 계약서 변환");
    },
  });
